import React, { useEffect } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { getColumns } from "src/utils/auth/getColumns";
import {
  resetState,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/PanFormReportSlice";
import { AppDispatch, RootState } from "src/redux/store";
import {
  useExportPanFormPendingXlsxMutation,
  useGetPanFormPendingReportQuery,
} from "src/services/PanFormReportService";
import { formatDateAndTime } from "src/utils/dateAndTime";
import { showToast } from "src/utils/toaster/showToast";
import PanFormReportListing from "./PanFormReportListing";

const paramList = [
  "name",
  "srn",
  "email",
  "mobileNumber",
  "adhaarNumber",
  "panNumber",
  "acknowledgementNumber",
  "distributorCode",
  "status",
];

const PanFormReportListingWrapper = () => {
  const dispatch = useDispatch<AppDispatch>();
  const panFormReportState: any = useSelector(
    (state: RootState) => state.panFormReport
  );
  const { items, searchValue, rowsPerPage, page, filterBy, dateFilter, orderByValue } =
    panFormReportState;

  useEffect(() => {
    dispatch(resetState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isFetching, isLoading } = useGetPanFormPendingReportQuery({
    limit: rowsPerPage,
    searchValue,
    params: paramList,
    page,
    filterBy,
    dateFilter,
    orderBy: "createdDateToShow",
    orderByValue,
    isPaginationRequired: true,
  });

  React.useEffect(() => {
    if (!isFetching && !isLoading) {
      dispatch(setIsTableLoading(false));
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching, data]);

  const [exportXlsx, { isLoading: isExporting }] =
    useExportPanFormPendingXlsxMutation();

  /**
   * Exports the whole filtered set (not just the page on screen). The server
   * streams a real .xlsx, so all this has to do is save the blob.
   */
  const handleExportXlsx = async () => {
    try {
      const response: any = await exportXlsx({
        limit: 0,
        searchValue,
        params: paramList,
        page: 1,
        filterBy,
        dateFilter,
        orderBy: "createdDateToShow",
        orderByValue,
        isPaginationRequired: false,
      });

      if (!response?.data) {
        showToast("error", "Failed to export XLSX");
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `PAN_Form_Pending_${moment().format("DD-MM-YYYY_HH-mm")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast("success", "XLSX exported successfully");
    } catch (error) {
      console.error("Error exporting XLSX:", error);
      showToast("error", "Failed to export XLSX");
    }
  };

  const columns: columnTypes[] = [
    {
      field: "createdDateToShow",
      headerName: "Applied Date",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          <div className="text-slate-700 font-medium">
            {formatDateAndTime(row?.createdDateToShow || row?.createdAt, "DD MMM yyyy")}
          </div>
          <div className="text-[13px] font-medium text-slate-400">
            {formatDateAndTime(row?.createdDateToShow || row?.createdAt, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "srn",
      headerName: "SRN",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => (
        <span className="text-primary-main text-[13px] text-ellipsis overflow-hidden">
          {row?.srn || "-"}
        </span>
      ),
    },
    {
      field: "acknowledgementNumber",
      headerName: "Acknowledgement No.",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => (
        <Tooltip title={row?.acknowledgementNumber || ""}>
          <span className="text-[13px] text-ellipsis overflow-hidden">
            {row?.acknowledgementNumber || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => (
        <span className="text-ellipsis overflow-hidden">{row?.name || "-"}</span>
      ),
    },
    {
      field: "mobileNumber",
      headerName: "Mobile",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <span>{row?.mobileNumber || "-"}</span>,
    },
    {
      field: "applicationType",
      headerName: "Type",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <span>{row?.applicationType || "-"}</span>,
    },
    {
      field: "status",
      headerName: "Status",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <span>{row?.status || "-"}</span>,
    },
    {
      field: "distributorCode",
      headerName: "SJBT Code",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <span>{row?.distributorCode || "N/A"}</span>,
    },
    {
      field: "assignedToName",
      headerName: "Assignee",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => (
        <div className="text-primary-main py-[10px]">
          {row?.assignedToName || "Not Assigned"}
        </div>
      ),
    },
  ];

  return (
    <SideNavLayout>
      <PanFormReportListing
        columns={getColumns(columns, "PAN_FORM_DOWNLOAD_REPORT")}
        rows={items.map((item: any) => ({ ...item, key: item._id || item.srn }))}
        onExportXlsx={handleExportXlsx}
        isExporting={isExporting}
      />
    </SideNavLayout>
  );
};

export default PanFormReportListingWrapper;
